import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
  Avatar,
  Rating,
} from '@mui/material';
import {
  DirectionsBoat, 
  Flight, 
  Home as HomeIcon, 
  ShoppingCart,
  FormatQuote 
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const services = [
  {
    title: 'Groupage Maritime',
    icon: DirectionsBoat,
    description: 'Service de groupage maritime entre la France et la Côte d\'Ivoire',
    link: '/services#maritime'
  },
  {
    title: 'Groupage Aérien',
    icon: Flight,
    description: 'Transport aérien rapide et sécurisé de vos colis',
    link: '/services#air'
  },
  {
    title: 'Déménagement International',
    icon: HomeIcon,
    description: 'Solutions complètes pour votre déménagement France-Côte d\'Ivoire',
    link: '/services#moving'
  },
  {
    title: 'Achat International',
    icon: ShoppingCart,
    description: 'Service d\'achat et d\'importation depuis la Chine vers la Côte d\'Ivoire',
    link: '/services#shopping'
  }
];

const slides = [
  {
    image: require('../assets/images/slide1.jpg'),
    title: 'Transport Maritime International',
    description: 'Solutions de transport maritime fiables entre la France et la Côte d\'Ivoire'
  },
  {
    image: require('../assets/images/slide2.jpg'),
    title: 'Expertise Logistique',
    description: 'Une équipe expérimentée pour gérer vos expéditions internationales'
  },
  {
    image: require('../assets/images/slide3.jpg'),
    title: 'Réseau International',
    description: 'Un réseau logistique étendu pour des livraisons efficaces'
  },
  {
    image: require('../assets/images/slide4.jpg'),
    title: 'Service Premium',
    description: 'Un service personnalisé et professionnel pour répondre à vos besoins spécifiques'
  }
];

const warehouseImages = [
  // Section Entrepôt et Manutention
  {
    url: require('../assets/images/warehouse1.jpg'),
    title: 'Zone de stockage sécurisée'
  },
  {
    url: require('../assets/images/warehouse9.jpg'),
    title: 'Chariot élévateur en action'
  },
  {
    url: require('../assets/images/warehouse10.jpg'),
    title: 'Entrepôt moderne'
  },
  {
    url: require('../assets/images/warehouse4.jpg'),
    title: 'Zone de tri'
  },
  {
    url: require('../assets/images/warehouse7.jpg'),
    title: 'Manutention'
  },
  {
    url: require('../assets/images/warehouse8.jpg'),
    title: 'Zone de stockage'
  },

  // Section Transport Maritime et Logistique
  {
    url: require('../assets/images/port1.jpg'),
    title: 'Terminal conteneurs'
  },
  {
    url: require('../assets/images/port2.jpg'),
    title: 'Porte-conteneurs'
  },
  {
    url: require('../assets/images/port3.jpg'),
    title: 'Vue aérienne du port'
  },
  {
    url: require('../assets/images/logistics1.jpg'),
    title: 'Transport routier'
  }
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, 30000); // Change every 30 seconds

    return () => clearInterval(timer);
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Paper
        sx={{
          position: 'relative',
          backgroundColor: 'grey.800',
          color: '#fff',
          mb: 4,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundImage: `url('${slides[currentSlide].image}')`,
          height: { xs: '50vh', sm: '60vh', md: '70vh' },
          transition: 'background-image 1s ease-in-out',
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,.1)',
          }}
        />
        <Grid container>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                p: { xs: 2, sm: 4, md: 6 },
                pr: { md: 0 },
                pt: { xs: 4, sm: 8, md: 15 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Typography 
                component="h1" 
                variant="h3" 
                color="inherit" 
                gutterBottom
                sx={{ 
                  transition: 'opacity 0.5s ease-in-out',
                  animation: 'fadeIn 1s ease-in-out',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                {slides[currentSlide].title}
              </Typography>
              <Typography 
                variant="h5" 
                color="inherit" 
                paragraph
                sx={{ 
                  transition: 'opacity 0.5s ease-in-out',
                  animation: 'fadeIn 1s ease-in-out',
                  fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {slides[currentSlide].description}
              </Typography>
              <Button
                component={RouterLink}
                to="/services"
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
              >
                Découvrir nos services
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Slide indicators */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 2,
          }}
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: currentSlide === index ? 'white' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
              }}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </Box>
      </Paper>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom>
          Nos Services
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={service.title}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      transition: 'transform 0.3s ease-in-out',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: 'primary.main',
                      color: 'white',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 40 }} />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2" align="center">
                      {service.title}
                    </Typography>
                    <Typography align="center">
                      {service.description}
                    </Typography>
                  </CardContent>
                  <Button
                    component={RouterLink}
                    to={service.link}
                    sx={{ m: 2 }}
                    variant="outlined"
                  >
                    En savoir plus
                  </Button>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Quick Actions Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom>
            Actions Rapides
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Demander un Devis
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ 
                      minHeight: '48px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 3
                    }}
                  >
                    Obtenez rapidement un devis personnalisé pour vos besoins de transport et logistique
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/quote"
                    fullWidth
                  >
                    Devis Gratuit
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Suivre un Colis
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ 
                      minHeight: '48px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 3
                    }}
                  >
                    Suivez en temps réel l'état et la position de vos colis pendant leur transport
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/tracking"
                    fullWidth
                  >
                    Suivre mon Colis
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Prendre Rendez-vous
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ 
                      minHeight: '48px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 3
                    }}
                  >
                    Planifiez une rencontre avec nos experts pour discuter de vos projets logistiques
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/appointment"
                    fullWidth
                  >
                    Réserver
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Warehouse Gallery */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom>
            Nos Installations
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            color="text.secondary" 
            sx={{ mb: 6 }}
          >
            Découvrez notre réseau logistique international
          </Typography>
          <Grid container spacing={3}>
            {/* Section Entrepôt */}
            <Grid item xs={12}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Entrepôt et Manutention
              </Typography>
            </Grid>
            {warehouseImages.slice(0, 6).map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '75%',
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: 2,
                    boxShadow: 3,
                    '&:hover': {
                      '& .overlay': {
                        opacity: 1,
                      },
                      '& img': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt={image.title}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  />
                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: 2,
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  >
                    <Typography variant="subtitle1" align="center">
                      {image.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}

            {/* Section Transport Maritime */}
            <Grid item xs={12} sx={{ mt: 6 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Transport Maritime et Logistique
              </Typography>
            </Grid>
            {warehouseImages.slice(6).map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={index + 6}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '75%',
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: 2,
                    boxShadow: 3,
                    '&:hover': {
                      '& .overlay': {
                        opacity: 1,
                      },
                      '& img': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt={image.title}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  />
                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: 2,
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  >
                    <Typography variant="subtitle1" align="center">
                      {image.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
